import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { PageHeader, cardWrap, hdrCell, ghostBtn, smallBtn } from "@/components/admin-ui";
import { useToast } from "@/components/ui-prims";
import { EditUnitDialog, type UnitPatchBody } from "./reconcile/EditUnitDialog";
import { SplitUnitDialog, type SplitBody } from "./reconcile/SplitUnitDialog";
import { MergeUnitsDialog, type MergeBody } from "./reconcile/MergeUnitsDialog";
import { ProjectDetailsSection } from "./reconcile/ProjectDetailsEditor";
import { BBoxEditor } from "./reconcile/BBoxEditor";

// Mirrors GET /admin/dataengine/reconcile/floorplan payload shape.

type Room = {
  label?: string;
  area_sqm?: number;
  bbox?: [number, number, number, number]; // [x_min, y_min, x_max, y_max], native image pixels
};

type FloorplanUnit = {
  id: number;
  unit_type: string;
  classification?: string | null;
  property_type?: string | null;
  n_storeys: number;
  bedrooms?: number | null;
  bathrooms?: number | null;
  total_area_sqm?: number | null;
  balcony_sqm?: number | null;
  facing?: string | null;
  rooms: Room[];
  windows?: unknown[];
  storeys?: unknown;
  n_source_records?: number | null;
  source_image_sha256s?: string[];
  source_image_basenames?: string[];
  match_methods?: string[];
};

type FloorplanProject = {
  slug: string;
  n_source_images: number;
  confidence?: number | null;
  aggregated_at: string;
  synced_at: string;
  n_physical_units?: number | null;
  n_multi_storey_units?: number | null;
  dominant_property_type?: string | null;
};

type SourceImage = {
  asset_sha256: string;
  oss_key: string;
  url?: string;
  content_type: string;
  bytes: number;
  uploaded_at: string;
  basename?: string;
};

type InspectorResponse = {
  slug: string;
  project: FloorplanProject;
  units: FloorplanUnit[];
  source_images: SourceImage[];
};

export function FloorplanInspectorPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [data, setData] = useState<InspectorResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
  const [mergeIds, setMergeIds] = useState<Set<number>>(() => new Set());
  const [auditRefresh, setAuditRefresh] = useState(0); // bumps to force HistoryPanel reload

  // Dialog open + busy state. Only one dialog open at a time so a single
  // busy flag covers all three.
  const [editOpen, setEditOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bboxOpen, setBboxOpen] = useState(false);
  const [writeBusy, setWriteBusy] = useState(false);

  const fetchInspector = useCallback(async () => {
    if (!slug) return;
    try {
      const resp = await api.get<InspectorResponse>(
        `/admin/dataengine/reconcile/floorplan?slug=${encodeURIComponent(slug)}`,
      );
      setData(resp);
      setErr(null);
      // Preserve current single-selection if it still exists; otherwise
      // default to the first unit. Drop any merge-selected ids that are
      // no longer in the row set (after split/merge the ids change).
      setSelectedUnitId((prev) =>
        prev != null && resp.units.some((u) => u.id === prev) ? prev : (resp.units[0]?.id ?? null)
      );
      setMergeIds((prev) => {
        const next = new Set<number>();
        for (const id of prev) {
          if (resp.units.some((u) => u.id === id)) next.add(id);
        }
        return next;
      });
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    setData(null); setErr(null);
    setActiveImageIdx(0);
    setMergeIds(new Set());
    fetchInspector();
  }, [slug, fetchInspector]);

  const selectedUnit = useMemo(
    () => data?.units.find((u) => u.id === selectedUnitId) ?? null,
    [data, selectedUnitId],
  );
  const mergeInputs = useMemo(
    () => (data?.units ?? []).filter((u) => mergeIds.has(u.id)),
    [data, mergeIds],
  );

  // Carousel images filter to the selected unit's bindings, so when the
  // operator focuses a unit they only paginate through the images that
  // actually back that unit. With no unit selected — or a unit with no
  // bindings — the carousel falls back to the full set (preferable to a
  // blank pane, and preserves the "browse all images" affordance).
  //
  // Filtering > "jump to first match" because units commonly have 2-3
  // bound images (per-storey plans on a duplex); jumping showed only
  // one and confused operators into thinking the rest were unbound.
  const displayImages = useMemo<SourceImage[]>(() => {
    if (!data) return [];
    if (!selectedUnit) return data.source_images;
    const targets = selectedUnit.source_image_sha256s ?? [];
    if (targets.length === 0) return data.source_images;
    return data.source_images.filter((img) => targets.includes(img.asset_sha256));
  }, [data, selectedUnit]);
  const totalImageCount = data?.source_images.length ?? 0;
  const filtered = displayImages.length !== totalImageCount;
  const activeImage = displayImages[activeImageIdx];

  // When the operator changes selection, reset the carousel to the
  // first image of the (now-filtered) set. Tracked via a ref so a data
  // refetch after an edit doesn't yank the operator back to image 1
  // unprompted.
  const lastSyncedUnitId = useRef<number | null>(null);
  useEffect(() => {
    if (!selectedUnit) return;
    if (lastSyncedUnitId.current === selectedUnit.id) return;
    lastSyncedUnitId.current = selectedUnit.id;
    setActiveImageIdx(0);
  }, [selectedUnit]);

  // Defensive: if the filter shrinks displayImages below the current
  // index (e.g., refetch lost an image that was the only bound one),
  // clamp back to the first available.
  useEffect(() => {
    if (activeImageIdx >= displayImages.length && displayImages.length > 0) {
      setActiveImageIdx(0);
    }
  }, [displayImages.length, activeImageIdx]);

  // Reverse link: which units reference the currently active image?
  // Drives the "used by" badge in the image footer so the operator can
  // see image↔unit attribution at a glance, in both directions. Reads
  // from the full data.units (not displayImages-derived) so the badge
  // surfaces every cross-unit reference even when the carousel is
  // filtered to one unit's bindings.
  const usedByUnitTypes = useMemo<string[]>(() => {
    if (!data || !activeImage) return [];
    return data.units
      .filter((u) => (u.source_image_sha256s ?? []).includes(activeImage.asset_sha256))
      .map((u) => u.unit_type || "—");
  }, [data, activeImage]);

  function toggleMergeId(id: number) {
    setMergeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handlePatch(body: UnitPatchBody) {
    if (!selectedUnit) return;
    setWriteBusy(true);
    try {
      await api.patch(`/admin/dataengine/reconcile/floorplan_unit/${selectedUnit.id}`, body);
      toast("Unit updated.", { tone: "success" });
      setEditOpen(false);
      await fetchInspector();
      setAuditRefresh((n) => n + 1);
    } catch (e: any) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setWriteBusy(false);
    }
  }

  async function handleSplit(body: SplitBody) {
    if (!selectedUnit) return;
    setWriteBusy(true);
    try {
      await api.post(`/admin/dataengine/reconcile/floorplan_unit/split?id=${selectedUnit.id}`, body);
      toast(`Split into ${body.splits.length} units.`, { tone: "success" });
      setSplitOpen(false);
      await fetchInspector();
      setAuditRefresh((n) => n + 1);
    } catch (e: any) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setWriteBusy(false);
    }
  }

  async function handleMerge(body: MergeBody) {
    setWriteBusy(true);
    try {
      await api.post(`/admin/dataengine/reconcile/floorplan_unit/merge`, body);
      toast(`Merged ${body.ids.length} units into 1.`, { tone: "success" });
      setMergeOpen(false);
      await fetchInspector();
      setAuditRefresh((n) => n + 1);
    } catch (e: any) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setWriteBusy(false);
    }
  }

  // Re-run the floorplan extractor for this slug (PROP-70). For when the
  // source image itself is the problem and hand-editing rows (above) is
  // the wrong fix. Extraction runs async on the data engine, so we
  // confirm the kick-off and point the operator at /dataengine/runs —
  // we do NOT refetch immediately (the rows won't have changed yet).
  async function handleReextract() {
    if (!slug || writeBusy) return;
    if (!window.confirm(
      `Re-run the floorplan extractor for "${slug}"?\n\nThis re-processes the source images and overwrites the extracted rows. It runs in the background — watch Data engine → Runs for progress.`,
    )) return;
    setWriteBusy(true);
    try {
      await api.post(`/admin/dataengine/run`, {
        source: "floorplan-extract",
        mode: "incremental",
        slug,
      });
      toast("Re-extraction started — watch Data engine → Runs, then refresh.", { tone: "success" });
    } catch (e: any) {
      toast(e instanceof ApiError ? e.message : String(e), { tone: "error" });
    } finally {
      setWriteBusy(false);
    }
  }

  const editEnabled = !!selectedUnit;
  const splitEnabled = !!selectedUnit && selectedUnit.total_area_sqm != null;
  const mergeEnabled = mergeIds.size >= 2;
  // Bbox editor needs both a unit and at least one source image with a
  // resolved public URL — without that, the canvas would render blind.
  const bboxEnabled = !!selectedUnit && !!activeImage?.url;

  return (
    <>
      <PageHeader
        kicker="Reconcile"
        title={data?.project ? slug : "Inspector"}
        sub={
          data
            ? `${data.units.length} unit row${data.units.length === 1 ? "" : "s"} · ${data.source_images.length} source image${data.source_images.length === 1 ? "" : "s"} · confidence ${
                data.project.confidence == null ? "—" : data.project.confidence.toFixed(2)
              }`
            : "Read-only view of the floorplan extraction backing this slug."
        }
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            {data?.project && (
              <button
                type="button"
                onClick={handleReextract}
                disabled={writeBusy}
                style={{ ...smallBtn, opacity: writeBusy ? 0.6 : 1 }}
                title="Re-process the source images and overwrite the extracted rows (runs in the background)"
              >
                ⟳ Re-run extractor
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/dataengine/reconcile")}
              style={ghostBtn}
            >
              ← Back to queue
            </button>
          </div>
        }
      />

      {err && (
        <div style={{
          margin: "0 26px 14px", background: "rgba(122,45,31,0.08)", color: "var(--neg)",
          padding: "10px 14px", borderRadius: 8, fontSize: 13,
        }}>
          {err === "no floorplan extraction for slug"
            ? <>This slug has no <code>floorplan_projects</code> row yet. <Link to="/dataengine/reconcile" style={{ color: "var(--accent)" }}>Back to queue</Link>.</>
            : err}
        </div>
      )}

      {data && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) minmax(360px, 1fr)",
          gap: 14, margin: "0 26px 22px",
        }}>
          {/* LEFT: image + bbox overlay */}
          <div style={{ ...cardWrap, margin: 0, padding: 0 }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={hdrCell}>Source image</div>
                {filtered && (
                  <span style={{
                    fontFamily: "var(--font-mono)", fontSize: 10.5,
                    color: "var(--ink-4)", padding: "1px 6px", borderRadius: 3,
                    background: "var(--accent-soft)",
                  }}>
                    filtered to {displayImages.length}/{totalImageCount} bound to {selectedUnit?.unit_type || "selected unit"}
                  </span>
                )}
              </div>
              {displayImages.length > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setActiveImageIdx((i) => Math.max(0, i - 1))}
                    disabled={activeImageIdx <= 0}
                    style={{ ...ghostBtn, padding: "3px 8px", fontSize: 11 }}
                  >‹</button>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>
                    {activeImageIdx + 1} / {displayImages.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveImageIdx((i) => Math.min(displayImages.length - 1, i + 1))}
                    disabled={activeImageIdx >= displayImages.length - 1}
                    style={{ ...ghostBtn, padding: "3px 8px", fontSize: 11 }}
                  >›</button>
                </div>
              )}
            </div>
            <ImageWithOverlay
              image={activeImage}
              preloadImages={displayImages}
              rooms={selectedUnit?.rooms ?? []}
            />
            {activeImage && (
              <div style={{ padding: "8px 14px", borderTop: "1px solid var(--rule)", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)", display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {activeImage.basename || activeImage.asset_sha256.slice(0, 12)}
                  </span>
                  <span>{fmtBytes(activeImage.bytes)}</span>
                </div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
                  Used by:{" "}
                  {usedByUnitTypes.length === 0
                    ? <em style={{ color: "var(--ink-4)" }}>no unit attributes this image</em>
                    : usedByUnitTypes.map((ut, i) => (
                        <span key={i} style={{
                          display: "inline-block", marginRight: 4,
                          padding: "1px 6px", borderRadius: 3,
                          background: ut === selectedUnit?.unit_type ? "var(--accent-soft)" : "var(--surface-2)",
                          color: ut === selectedUnit?.unit_type ? "var(--accent-ink, #0e2c22)" : "var(--ink-2)",
                          fontWeight: ut === selectedUnit?.unit_type ? 600 : 500,
                          fontSize: 10.5,
                        }}>{ut}</span>
                      ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: per-unit table + edit toolbar */}
          <div style={{ ...cardWrap, margin: 0 }}>
            <div style={{ padding: "8px 14px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={hdrCell}>Units · {data.units.length}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  style={{ ...smallBtn, opacity: editEnabled ? 1 : 0.4, cursor: editEnabled ? "pointer" : "not-allowed" }}
                  disabled={!editEnabled}
                  onClick={() => setEditOpen(true)}
                  title="Edit the selected unit (PATCH)"
                >Edit</button>
                <button
                  type="button"
                  style={{ ...smallBtn, opacity: splitEnabled ? 1 : 0.4, cursor: splitEnabled ? "pointer" : "not-allowed" }}
                  disabled={!splitEnabled}
                  onClick={() => setSplitOpen(true)}
                  title="Split the selected unit into N children"
                >Split</button>
                <button
                  type="button"
                  style={{ ...smallBtn, opacity: mergeEnabled ? 1 : 0.4, cursor: mergeEnabled ? "pointer" : "not-allowed" }}
                  disabled={!mergeEnabled}
                  onClick={() => setMergeOpen(true)}
                  title="Merge the checked units into one"
                >Merge {mergeIds.size > 0 ? `(${mergeIds.size})` : ""}</button>
                <button
                  type="button"
                  style={{ ...smallBtn, opacity: bboxEnabled ? 1 : 0.4, cursor: bboxEnabled ? "pointer" : "not-allowed" }}
                  disabled={!bboxEnabled}
                  onClick={() => setBboxOpen(true)}
                  title="Edit room bboxes on the source image"
                >Bboxes</button>
              </div>
            </div>
            <div style={hdrRow}>
              <div />
              <div style={hdrCell}>Unit</div>
              <div style={{ ...hdrCell, textAlign: "right" }}>Area</div>
              <div style={{ ...hdrCell, textAlign: "right" }}>Bd/Ba</div>
              <div style={{ ...hdrCell, textAlign: "right" }}>Storeys</div>
            </div>
            {data.units.map((u) => {
              const active = u.id === selectedUnitId;
              const checked = mergeIds.has(u.id);
              return (
                <div
                  key={u.id}
                  className="ops-row"
                  style={{ ...dataRow, background: active ? "var(--accent-soft)" : undefined }}
                  onClick={() => setSelectedUnitId(u.id)}
                >
                  <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMergeId(u.id)}
                      aria-label={`Select ${u.unit_type} for merge`}
                    />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.unit_type || "—"}
                    </div>
                    <div style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
                      {u.classification ?? "—"}
                      {u.property_type ? ` · ${u.property_type}` : ""}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    {u.total_area_sqm == null ? "—" : `${u.total_area_sqm} m²`}
                  </div>
                  <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    {fmtBdBa(u.bedrooms, u.bathrooms)}
                  </div>
                  <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    {u.n_storeys}
                  </div>
                </div>
              );
            })}
            {data.units.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>
                No <code>floorplan_units</code> rows for this slug.
              </div>
            )}

            {selectedUnit && (
              <RoomList unit={selectedUnit} />
            )}
          </div>

          {/* project_details + URA-canonical editor (S5). Full-width
              section between the floorplan panes and the history feed,
              so the operator sees cos↔URA reconcile state and the
              floorplan extraction together. */}
          <div style={{ gridColumn: "1 / -1" }}>
            <ProjectDetailsSection
              slug={slug}
              onSaved={() => setAuditRefresh((n) => n + 1)}
            />
          </div>

          {/* History panel spans the full grid; lives under the two
              panes so an operator can scroll down to the audit trail
              without losing the image/unit context above. */}
          <div style={{ gridColumn: "1 / -1" }}>
            <HistoryPanel slug={slug} refresh={auditRefresh} />
          </div>
        </div>
      )}

      {!data && !err && (
        <div style={{ margin: "0 26px", padding: 24, color: "var(--ink-3)", fontStyle: "italic" }}>
          Loading…
        </div>
      )}

      <EditUnitDialog
        open={editOpen}
        unit={selectedUnit}
        busy={writeBusy}
        onSubmit={handlePatch}
        onCancel={() => !writeBusy && setEditOpen(false)}
      />
      <SplitUnitDialog
        open={splitOpen}
        unit={selectedUnit}
        busy={writeBusy}
        onSubmit={handleSplit}
        onCancel={() => !writeBusy && setSplitOpen(false)}
      />
      <MergeUnitsDialog
        open={mergeOpen}
        inputs={mergeInputs}
        busy={writeBusy}
        onSubmit={handleMerge}
        onCancel={() => !writeBusy && setMergeOpen(false)}
      />
      {selectedUnit && (
        <BBoxEditor
          open={bboxOpen}
          unitId={selectedUnit.id}
          unitType={selectedUnit.unit_type}
          imageUrl={activeImage?.url}
          initialRooms={selectedUnit.rooms ?? []}
          onCancel={() => setBboxOpen(false)}
          onSaved={async () => {
            setBboxOpen(false);
            await fetchInspector();
            setAuditRefresh((n) => n + 1);
          }}
        />
      )}
    </>
  );
}

/* ─── Image + bbox overlay ─────────────────────────────────────────── */

function ImageWithOverlay({ image, preloadImages, rooms }: { image: SourceImage | undefined; preloadImages: SourceImage[]; rooms: Room[] }) {
  // We render the image at its natural size scaled to fit the container
  // width, and overlay an absolutely-positioned canvas. Storing native
  // pixel coords in DB but scaling client-side keeps the source of truth
  // pixel-accurate — same approach the bbox editor (S4) uses.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imgState, setImgState] = useState<{ w: number; h: number; loaded: boolean }>({ w: 0, h: 0, loaded: false });

  // Reset the loaded flag whenever the src changes so the spinner
  // overlay reappears during the next fetch. Without this the previous
  // image's loaded=true would carry over and the spinner would never
  // show on subsequent carousel switches.
  useEffect(() => {
    setImgState({ w: 0, h: 0, loaded: false });
  }, [image?.asset_sha256]);

  // drawOverlay needs to be stable for the ResizeObserver but also see
  // fresh `rooms` / `imgState` after re-renders. useCallback with those
  // deps gives both — the RO effect re-binds when the function changes,
  // which is what we want (otherwise the RO would call a stale closure
  // and never reflect new bboxes after a unit-selection change).
  const drawOverlay = useCallback(() => {
    const img = imgRef.current;
    const cvs = canvasRef.current;
    if (!img || !cvs || !imgState.loaded || imgState.w === 0) return;
    const rect = img.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    cvs.width = rect.width * dpr;
    cvs.height = rect.height * dpr;
    cvs.style.width = `${rect.width}px`;
    cvs.style.height = `${rect.height}px`;
    const ctx = cvs.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);
    const sx = rect.width / imgState.w;
    const sy = rect.height / imgState.h;
    ctx.strokeStyle = "rgba(122,45,31,0.9)";
    ctx.fillStyle = "rgba(122,45,31,0.08)";
    ctx.lineWidth = 1.5;
    ctx.font = "11px ui-sans-serif, system-ui";
    for (const r of rooms) {
      if (!r.bbox || r.bbox.length !== 4) continue;
      const [x0, y0, x1, y1] = r.bbox;
      const x = x0 * sx, y = y0 * sy;
      const w = (x1 - x0) * sx, h = (y1 - y0) * sy;
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      if (r.label) {
        ctx.fillStyle = "rgba(23,32,29,0.85)";
        ctx.fillRect(x, y - 14, ctx.measureText(r.label).width + 10, 14);
        ctx.fillStyle = "#f4f1ea";
        ctx.fillText(r.label, x + 4, y - 3);
        ctx.fillStyle = "rgba(122,45,31,0.08)";
      }
    }
  }, [rooms, imgState.loaded, imgState.w, imgState.h]);

  // Resize observer keeps the canvas size in sync with the rendered img.
  // Deps include drawOverlay so we re-bind whenever the closure refreshes;
  // the cleanup disconnects the previous observer first, so no leak.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => drawOverlay());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [drawOverlay]);

  // Re-draw whenever the closure refreshes (rooms / load state / size change).
  useEffect(() => { drawOverlay(); }, [drawOverlay]);

  if (!image) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>
        No source images linked to this slug.
      </div>
    );
  }
  if (!image.url) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontStyle: "italic" }}>
        Image URL unavailable — set <code>OSS_PUBLIC_BASE</code> on the admin server to render imagery.
      </div>
    );
  }
  // Browser-cache priming for the other bound images. As the operator
  // navigates the filtered carousel the next click hits a cached blob
  // instead of a fresh fetch. width/height/visibility kept zeroed so
  // these img tags don't occupy layout space or get exposed to AT.
  const preloadSrcs = preloadImages
    .filter((img) => img.url && img.asset_sha256 !== image.asset_sha256)
    .map((img) => img.url as string);

  return (
    <div ref={wrapRef} style={{ position: "relative", background: "var(--surface-2)", minHeight: 200 }}>
      <img
        ref={imgRef}
        src={image.url}
        alt={image.basename || image.asset_sha256}
        style={{ display: "block", width: "100%", height: "auto" }}
        onLoad={(e) => {
          const el = e.currentTarget;
          setImgState({ w: el.naturalWidth, h: el.naturalHeight, loaded: true });
        }}
      />
      {!imgState.loaded && <LoadingOverlay label={image.basename} />}
      <canvas
        ref={canvasRef}
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      />
      {/* Hidden preload sleeve — browser fetches + caches every other
          bound image so the next carousel click is instant. Zero-sized
          and aria-hidden so it never reaches layout or screen readers. */}
      <div aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}>
        {preloadSrcs.map((src) => (
          <img key={src} src={src} alt="" />
        ))}
      </div>
    </div>
  );
}

// LoadingOverlay covers the image pane with a centred SVG spinner while
// the current source image is fetching. Self-contained SVG so we don't
// have to inject a global keyframe — animateTransform handles the
// rotation in-element.
function LoadingOverlay({ label }: { label?: string }) {
  return (
    <div style={{
      position: "absolute", inset: 0,
      background: "rgba(245, 243, 238, 0.55)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 8, pointerEvents: "none",
    }}>
      <svg width="34" height="34" viewBox="0 0 50 50" aria-label="Loading image">
        <circle
          cx="25" cy="25" r="20"
          fill="none" stroke="rgba(122,45,31,0.18)" strokeWidth="4"
        />
        <path
          d="M25 5 a20 20 0 0 1 20 20"
          fill="none" stroke="rgba(122,45,31,0.85)" strokeWidth="4" strokeLinecap="round"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 25 25" to="360 25 25"
            dur="0.9s" repeatCount="indefinite"
          />
        </path>
      </svg>
      {label && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-3)" }}>
          loading {label}…
        </div>
      )}
    </div>
  );
}

/* ─── History panel (reconcile_audit_log scoped to this slug) ─────── */

type AuditRow = {
  id: number;
  admin_email: string;
  admin_name: string;
  action: string;
  target_table: string;
  target_key: string;
  before?: unknown;
  after?: unknown;
  reason: string;
  created_at: string;
};

function HistoryPanel({ slug, refresh }: { slug: string; refresh: number }) {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await api.get<{ rows: AuditRow[] }>(
          `/admin/dataengine/reconcile/audit?slug=${encodeURIComponent(slug)}&limit=20`,
        );
        if (!cancelled) {
          setRows(data.rows ?? []);
          setErr(null);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, refresh]);

  return (
    <div style={{ ...cardWrap, margin: 0 }}>
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={hdrCell}>History — recent edits</div>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)" }}>
          {rows.length === 0 ? "none yet" : `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`}
        </span>
      </div>
      {err && (
        <div style={{ padding: "10px 14px", color: "var(--neg)", fontSize: 12 }}>{err}</div>
      )}
      {!err && loading && (
        <div style={{ padding: "10px 14px", color: "var(--ink-3)", fontSize: 12, fontStyle: "italic" }}>Loading…</div>
      )}
      {!err && !loading && rows.length === 0 && (
        <div style={{ padding: "16px 14px", color: "var(--ink-3)", fontSize: 12, fontStyle: "italic" }}>
          No edits recorded for this slug yet. Edits land here once the S3/S4/S5 write paths ship.
        </div>
      )}
      {rows.map((r, i) => (
        <div key={r.id} style={{
          display: "grid", gridTemplateColumns: "160px 1fr 160px 90px",
          gap: 10, padding: "10px 14px", alignItems: "start",
          borderTop: i === 0 ? undefined : "1px solid var(--rule)",
          fontSize: 12,
        }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)" }}>
            {fmtTime(r.created_at)}
          </div>
          <div style={{ color: "var(--ink-2)", minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{r.action}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 2 }}>{r.reason}</div>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)" }}>
            {r.target_table} · {r.target_key}
          </div>
          <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)" }}>
            {r.admin_name || r.admin_email}
          </div>
        </div>
      ))}
    </div>
  );
}

function fmtTime(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-SG", { dateStyle: "short", timeStyle: "short" });
}

function RoomList({ unit }: { unit: FloorplanUnit }) {
  if (!unit.rooms || unit.rooms.length === 0) {
    return (
      <div style={{ padding: "10px 14px", borderTop: "1px solid var(--rule)", color: "var(--ink-3)", fontSize: 11.5, fontStyle: "italic" }}>
        No rooms recorded for {unit.unit_type}.
      </div>
    );
  }
  const grounded = unit.rooms.filter((r) => Array.isArray(r.bbox) && r.bbox.length === 4).length;
  return (
    <div style={{ borderTop: "1px solid var(--rule)" }}>
      <div style={{ padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={hdrCell}>Rooms — {unit.unit_type}</div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)" }}>
          {grounded}/{unit.rooms.length} grounded
        </div>
      </div>
      <div style={{ padding: "0 14px 12px" }}>
        {unit.rooms.map((r, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "4px 0", borderTop: i === 0 ? undefined : "1px dashed var(--rule)",
            fontSize: 12,
          }}>
            <span style={{ color: "var(--ink)" }}>{r.label ?? "—"}</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>
              {r.area_sqm != null ? `${r.area_sqm} m²` : "—"}
              {r.bbox ? " · bbox" : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Styles + helpers ─────────────────────────────────────────────── */

// 5 columns: checkbox · unit (1.5fr) · area · bd/ba · storeys. Keep the
// header row in sync with dataRow when changing this.
const gridCols = "32px 1.5fr 90px 90px 80px";

const hdrRow: React.CSSProperties = {
  display: "grid", gridTemplateColumns: gridCols, gap: 10,
  padding: "10px 14px", background: "var(--surface-2)",
  borderBottom: "1px solid var(--rule)",
};
const dataRow: React.CSSProperties = {
  display: "grid", gridTemplateColumns: gridCols, gap: 10,
  padding: "9px 14px", alignItems: "center",
  borderBottom: "1px solid var(--rule)", cursor: "pointer",
};

function fmtBdBa(bd?: number | null, ba?: number | null): string {
  if (bd == null && ba == null) return "—";
  return `${bd ?? "—"} / ${ba ?? "—"}`;
}
function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}
